// Shared product-edit modal SHELL (2026-07-24).
//
// Extracted from the presentation ProductEditModal (PR #152) so the estimate /
// sales-order line editor can wear the identical chrome — tabbed popup, header
// summary, ESC-to-close, backdrop-click-to-close — while each surface keeps its
// OWN tab CONTENTS (the presentation item model and the order line model differ,
// so the bodies stay per-surface). The presentation modal is byte-compatible in
// behavior after this extraction: it renders the same header, the same tab
// strip, and the same footer, just sourced from here.
//
// The shell owns ONLY the chrome. It takes a `tabs` descriptor (id / label /
// optional badge / active / onSelect) and renders the strip; the caller renders
// the active tab's body as `children`. This keeps the money / decoration / image
// logic entirely on the caller side.

import { useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";

export interface ShellTab {
  id: string;
  label: string;
  /** Optional count pill shown after the label (e.g. imprint / charge count). */
  badge?: number;
  active: boolean;
  onSelect: () => void;
}

export function ProductEditModalShell(props: {
  ariaLabel: string;
  imageUrl?: string | undefined;
  title: string;
  subtitle: React.ReactNode;
  tabs: ShellTab[];
  onClose: () => void;
  children: React.ReactNode;
  /** Footer content; defaults to a single "Done" button that closes. */
  footer?: React.ReactNode;
  /** Near full screen width (the one-page quote item window, Stephanie
   *  2026-10-14 — Jake reviews on a ~1900px screen). Default stays 1080px. */
  wide?: boolean;
}) {
  const { ariaLabel, imageUrl, title, subtitle, tabs, onClose, children, footer, wide } =
    props;

  // Close on ESC.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brown-500/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={(e) => {
        // Click on the backdrop closes; clicks bubbled up from the inner panel
        // are stopped via stopPropagation below.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          "flex max-h-[92vh] w-full flex-col rounded-xl bg-white shadow-2xl",
          wide ? "max-w-[1600px]" : "max-w-[1080px]",
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — product summary */}
        <div className="flex items-start justify-between gap-4 border-b border-brown-50 px-6 py-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-14 w-14 flex-shrink-0 overflow-hidden rounded-md border border-brown-50 bg-cream-100">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : null}
            </div>
            <div className="min-w-0">
              <h2 className="truncate font-display text-xl font-semibold text-brown-500">
                {title}
              </h2>
              <p className="mt-0.5 text-xs text-brown-300">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-brown-300 hover:bg-cream-50 hover:text-brown-500"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab strip — omitted entirely when the caller passes no tabs.
            The presentation product editor is now ONE screen (CEO 2026-08-18:
            everything in one place rather than tab by tab), so it hands over an
            empty list and renders its sections stacked. The estimate /
            sales-order line editor still passes tabs and is untouched — which
            is the reason this is a conditional here rather than a deletion. */}
        {tabs.length > 0 && (
          <div className="flex gap-1 border-b border-brown-50 bg-cream-50/40 px-3 pt-3">
            {tabs.map((t) => (
              <ModalTabButton key={t.id} active={t.active} onClick={t.onSelect}>
                {t.label}
                {t.badge !== undefined && t.badge > 0 && (
                  <ModalTabPill>{t.badge}</ModalTabPill>
                )}
              </ModalTabButton>
            ))}
          </div>
        )}

        {/* Tab body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-brown-50 bg-cream-50/40 px-6 py-3">
          {footer ?? (
            <Button type="button" size="sm" onClick={onClose}>
              Done
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function ModalTabButton(props: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        "rounded-t-md px-3 py-2 text-xs font-semibold uppercase tracking-wide",
        props.active
          ? "border-b-2 border-ink bg-white text-ink"
          : "text-brown-400 hover:text-brown-500",
      )}
    >
      {props.children}
    </button>
  );
}

export function ModalTabPill(props: { children: React.ReactNode }) {
  return (
    <span className="ml-1.5 rounded-full bg-peach-100 px-1.5 py-0.5 text-[10px] font-semibold text-brown-500">
      {props.children}
    </span>
  );
}
